import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Lock, CheckCircle } from 'lucide-react';

const FAVICON_URL = "https://customer-assets.emergentagent.com/job_multi-watt-hub/artifacts/q9v3nz6d_favicon-riding-watt.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error('Minimum 6 caractères');
    if (newPassword !== confirmPassword) return toast.error('Les mots de passe ne correspondent pas');
    setLoading(true);
    try {
      await authAPI.resetPassword({ token, new_password: newPassword });
      setSuccess(true);
      toast.success('Mot de passe réinitialisé !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#0A0A0A]">
        <div className="text-center">
          <p className="text-red-400 text-lg">Lien invalide.</p>
          <Button onClick={() => navigate('/auth')} className="mt-4 bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold">
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-[#0A0A0A]" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={FAVICON_URL} alt="Riding Watt" className="w-14 h-14 rounded-2xl mx-auto mb-4 shadow-[0_0_30px_rgba(0,255,0,0.3)]" />
          <h1 className="font-heading text-2xl font-bold text-white">
            RIDING<span className="text-[#00FF00]">WATT</span>
          </h1>
        </div>

        <div className="glass-card rounded-2xl p-8 shadow-float">
          {success ? (
            <div className="text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-[#00FF00] mx-auto" />
              <h2 className="text-white text-xl font-bold">Mot de passe réinitialisé !</h2>
              <p className="text-gray-400">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
              <Button onClick={() => navigate('/auth')} className="w-full bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold py-6" data-testid="go-to-login">
                Se connecter
              </Button>
            </div>
          ) : (
            <form onSubmit={handleReset} className="space-y-4">
              <h2 className="text-white text-lg font-bold">Nouveau mot de passe</h2>
              <p className="text-gray-400 text-sm">Choisissez un nouveau mot de passe (minimum 6 caractères).</p>
              <div>
                <Label className="text-sm font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> Nouveau mot de passe
                </Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min. 6 caractères" required
                  className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                  data-testid="reset-new-password" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> Confirmer
                </Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmez le mot de passe" required
                  className="h-12 rounded-lg bg-[#1A1A1A] border-[#2A2A2A] text-white placeholder:text-slate-600"
                  data-testid="reset-confirm-password" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_20px_rgba(0,255,0,0.3)] py-6 font-bold"
                data-testid="reset-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Réinitialiser'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
